// Clinic status enum values
export const ClinicStatus = {
  Draft: 0,
  Active: 1,
  Inactive: 2,
  PendingVerification: 3,
} as const;

export type ClinicStatus = typeof ClinicStatus[keyof typeof ClinicStatus];

export const ClinicStatusLabels: Record<ClinicStatus, string> = {
  [ClinicStatus.Draft]: 'Draft',
  [ClinicStatus.Active]: 'Active',
  [ClinicStatus.Inactive]: 'Inactive',
  [ClinicStatus.PendingVerification]: 'Pending Verification',
};

export const ClinicStatusColors: Record<ClinicStatus, string> = {
  [ClinicStatus.Draft]: 'gray',
  [ClinicStatus.Active]: 'green',
  [ClinicStatus.Inactive]: 'red',
  [ClinicStatus.PendingVerification]: 'orange',
};

// Clinic capability enums (using const objects for TypeScript erasableSyntaxOnly compatibility)
export const FitTestType = {
  None: '',
  Qualitative: 'Qualitative',
  Quantitative: 'Quantitative',
  Both: 'Both'
} as const;

export const MaskType = {
  None: '',
  Half: 'Half',
  Full: 'Full',
  Both: 'Both'
} as const;

export const ClinicType = {
  OccupationalHealth: 0,
  UrgentCare: 1,
  Hospital: 2,
  Chiropractor: 3,
  PrimaryCare: 4,
  Specialist: 5,
} as const;

export type ClinicTypeValue = typeof ClinicType[keyof typeof ClinicType];

export const ClinicTypeLabels: Record<number, string> = {
  [ClinicType.OccupationalHealth]: 'Occupational Health',
  [ClinicType.UrgentCare]: 'Urgent Care',
  [ClinicType.Hospital]: 'Hospital',
  [ClinicType.Chiropractor]: 'Chiropractor',
  [ClinicType.PrimaryCare]: 'Primary Care',
  [ClinicType.Specialist]: 'Specialist',
};

export const PreferredMethodOfAppointment = {
  Call: 'Call',
  Email: 'Email',
  WalkIn: 'Walk-in'
} as const;

export const ChainOfCustodyProvider = {
  None: 'None',
  EScreen: 'eScreen',
  FormFox: 'FormFox',
  Quest: 'Quest'
} as const;

export const ReportingMethod = {
  Email: 'Email',
  Fax: 'Fax',
  Portal: 'Portal'
} as const;

export interface ClinicCategoryType {
  id: number;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

export interface ClinicCategoryDto {
  id: number;
  name: string;
}

export interface ClinicCapabilityType {
  id: number;
  name: string;
  key: string;
  displayOrder: number;
  isActive: boolean;
}

export interface ClinicListDto {
  id: number;
  name: string;
  code: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  isActive: boolean;
  location: string;
  status: ClinicStatus;
  statusDisplay: string;
  statusCssClass: string;
  lastVerifiedDate: string | null;
  lastVerifiedByUserName: string | null;
  // Capability flags for the grid-view badges. Derived server-side from the
  // capability / procedure lists (SLV2-3245); usp_GetClinics returns them.
  hasPAT?: boolean | null;
  hasXRay?: boolean | null;
  fitTestType?: string | null;
}

export interface ClinicWithDistance {
  id: number;
  name: string;
  code: string;
  city: string;
  state: string;
  phone: string;
  latitude: number | null;   // null when the clinic is not geocoded (by-ids results)
  longitude: number | null;
  distanceMiles: number | null;
  capabilityTypeIds?: string;   // comma-separated IDs e.g. "1,2,6,11"
  categoryNames?: string;       // comma-separated names e.g. "Urgent Care, Occ Med"
  categoryTypeIds?: string;     // comma-separated category type IDs e.g. "1,7,11"
  procedureIds?: string;        // comma-separated procedure (ServiceProcedure) IDs
}

export interface ClinicDetailDto {
  id: number;
  name: string;
  code: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  isActive: boolean;
  createdDate: string | null;
  modifiedDate: string | null;
  fullAddress: string;
  status: ClinicStatus;
  statusDisplay: string;
  statusCssClass: string;
  lastVerifiedDate: string | null;
  lastVerifiedByUserId: number | null;
  lastVerifiedByUserName: string | null;
  mailingStreet: string | null;
  mailingSuite: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  mailingCountry: string | null;
  shippingStreet: string | null;
  shippingSuite: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingZip: string | null;
  shippingCountry: string | null;
  // Geocoding (from dbo.Clinics mailing address)
  latitude: number | null;
  longitude: number | null;
  geocodeStatus: string | null;
  geocodedAt: string | null;
  // Operating hours
  monStart: string | null;
  monEnd: string | null;
  tueStart: string | null;
  tueEnd: string | null;
  wedStart: string | null;
  wedEnd: string | null;
  thurStart: string | null;
  thurEnd: string | null;
  friStart: string | null;
  friEnd: string | null;
  satStart: string | null;
  satEnd: string | null;
  sunStart: string | null;
  sunEnd: string | null;
  isER: boolean;
  isAfterHour: boolean;
  clinicTypes?: number | null;
  // Capability flags
  hasPAT?: boolean;
  fitTestType?: string;
  maskTypes?: string;
  hasXRay?: boolean;
  // Onboarding fields
  preferredMethodOfAppointment?: string;
  electronicChainOfCustody?: string; // CSV
  reportingMethod?: string;
  dotCertification?: boolean;
  numberOfCertifiedDOTProfessionals?: number;
  translationServices?: boolean;
  w9OnFile?: boolean;
  // External systems
  eScreenCode?: string;
  formFoxCode?: string;
  questCode?: string;
  // Free-text adapter types (shown when the 'adapters' capability is selected)
  adapterTypes?: string;
  // Categories and capability type IDs
  categories?: ClinicCategoryDto[];
  capabilityTypeIds?: number[];
}

export interface ClinicServiceFee {
  vendorFeeId: number;
  clinicId: number;
  procedureId: number;
  procedureCode: string;
  procedureName: string;
  fee: number;
  effectiveFrom: string;
  effectiveThru: string | null;
}

export interface ClinicProcedureDto {
  procedureId: number;
  procedureCode: string;
  procedureName: string;
  displayOrder: number;
  isAvailable: boolean;
  fee: number | null;
  effectiveFrom: string | null;
  effectiveThru: string | null;
}

export interface ServiceGroupWithProceduresDto {
  serviceGroupId: number;
  groupName: string;
  displayOrder: number;
  procedures: ClinicProcedureDto[];
}

export interface UpdateClinicServicesRequest {
  serviceGroupId?: number;
  /** SLV2-3452: fee is applied only for admins; null = keep the fee on file (new procedures start at 0). */
  services: { procedureId: number; fee: number | null }[];
}

export interface UpdateClinicRequest {
  name: string;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  mailingStreet: string | null;
  mailingSuite: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  // Capabilities
  hasPAT?: boolean;
  fitTestType?: string;
  maskTypes?: string;
  hasXRay?: boolean;
  // Onboarding
  preferredMethodOfAppointment?: string;
  electronicChainOfCustody?: string;
  reportingMethod?: string;
  dotCertification?: boolean;
  numberOfCertifiedDOTProfessionals?: number;
  translationServices?: boolean;
  w9OnFile?: boolean;
  // External
  eScreenCode?: string;
}

/**
 * Request for PUT /clinics/{id}/onboarding — the only endpoint that persists
 * the onboarding columns (vendor codes, ECoC providers, DOT cert, ...). The
 * proc overwrites every onboarding column it covers, so callers must send the
 * clinic's current values for fields they aren't editing. supportedLanguageIds
 * left undefined leaves languages untouched. Translation Services is NOT an
 * onboarding field any more (SLV2-3301): it is capability 10 and the clinic
 * column is derived from the capability list.
 */
export interface UpdateClinicOnboardingRequest {
  preferredMethodOfAppointment?: string;
  electronicChainOfCustody?: string; // CSV
  reportingMethod?: string;
  dotCertification?: boolean;
  numberOfCertifiedDOTProfessionals?: number;
  w9OnFile?: boolean;
  eScreenCode?: string;
  formFoxCode?: string;
  questCode?: string;
  supportedLanguageIds?: number[];
}

export interface CreateClinicRequest {
  name: string;
  code?: string;
  phone?: string;
  email?: string;
  includeAddress?: boolean;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  // Capabilities
  hasPAT?: boolean;
  fitTestType?: string;
  maskTypes?: string;
  hasXRay?: boolean;
  // Onboarding
  preferredMethodOfAppointment?: string;
  electronicChainOfCustody?: string;
  reportingMethod?: string;
  dotCertification?: boolean;
  numberOfCertifiedDOTProfessionals?: number;
  translationServices?: boolean;
  w9OnFile?: boolean;
  // External
  eScreenCode?: string;
}
